import { DownOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, Card, DatePicker, Dropdown, Input, message, Select, Space, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TableRowSelection } from 'antd/es/table/interface'
import dayjs, { Dayjs } from 'dayjs'
import { AccountVestingSearchParamsV2, AccountVestingTypeV2, AllocationItemV2 } from 'Modules'
import { useEffect, useState } from 'react'

import { accountVestingV2Api } from 'api/account-vesting-v2-api'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import {
  ACCOUNT_VESTING_STATUS_MAP_V2,
  ACCOUNT_VESTING_STATUS_OPTIONS_V2,
  AccountVestingStatusEnumV2,
  AccountVestingTypeEnum,
  ALLOCATION_TITLE,
} from 'constants/vesting'
import { useAccountVestingContextV2 } from 'context/account-vesting-context-provider-v2'
import { useAccountVestingDataV2 } from 'hooks/account-vesting/use-account-vesting-data-v2'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { mapContextTypeToApiType } from 'utils/account-vesting-v2'
import { logInfo } from 'utils/logger'

import AllocationDropdownCell from './allocation-dropdown-cell'

interface IAccountVestingContentV2Props {
  title?: React.ReactNode
}

const AccountVestingContentV2 = ({ title }: IAccountVestingContentV2Props) => {
  const { type } = useAccountVestingContextV2()

  const {
    loading,
    data,
    total,
    searchParams,
    getData,
    onSearch,
    initPagination,
    onChangePagination,
  } = useAccountVestingDataV2()

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null)
  const [dateTo, setDateTo] = useState<Dayjs | null>(null)

  const additionalItems = [BREADCRUMBS.VESTING_DATA]

  // Sync dateFrom and dateTo from searchParams
  useEffect(() => {
    setDateFrom(searchParams.dateFrom ? dayjs(searchParams.dateFrom) : null)
    setDateTo(searchParams.dateTo ? dayjs(searchParams.dateTo) : null)
  }, [searchParams.dateFrom, searchParams.dateTo])

  const handleDateFromChange = (date: Dayjs | null) => {
    setDateFrom(date)
    const dateFromValue = date ? date.valueOf() : undefined // timestamp in milliseconds with time
    onSearch({
      dateFrom: dateFromValue,
      dateTo: searchParams.dateTo, // Keep existing dateTo
    } as Partial<AccountVestingSearchParamsV2>)
  }

  const handleDateToChange = (date: Dayjs | null) => {
    setDateTo(date)
    const dateToValue = date ? date.valueOf() : undefined // timestamp in milliseconds with time
    onSearch({
      dateFrom: searchParams.dateFrom, // Keep existing dateFrom
      dateTo: dateToValue,
    } as Partial<AccountVestingSearchParamsV2>)
  }

  const isDisabledStatus = (status?: number | null) =>
    status === AccountVestingStatusEnumV2.EQUAL ||
    status === AccountVestingStatusEnumV2.CANCEL_CONTRACT

  const isFilterDisabledStatus = isDisabledStatus(searchParams.status)

  const handleSyncData = async () => {
    if (!type) {
      message.error('Vesting type is required')
      return
    }
    try {
      onOpenModalProcessing('Syncing data...')
      const res = await accountVestingV2Api.sync({ type: mapContextTypeToApiType(type) })
      if (res?.data?.success) {
        message.success('Sync data successfully')
        getData(searchParams)
      } else {
        message.error(res?.data?.message || 'Sync data failed')
      }
    } catch (error: any) {
      message.error(error?.message || 'Sync data failed')
      logInfo('Error: ', error)
    } finally {
      onCloseModalProcessing()
    }
  }

  const handleApproveAll = async () => {
    if (!type) {
      message.error('Vesting type is required')
      return
    }
    if (isFilterDisabledStatus) {
      message.error('Cannot approve items with status Equal or Cancel Contract')
      return
    }
    try {
      onOpenModalProcessing('Approving all items...')
      const res = await accountVestingV2Api.approve({ type: mapContextTypeToApiType(type) })
      if (res?.data?.success) {
        message.success('Approve all successfully')
        getData(searchParams)
      } else {
        message.error(res?.data?.message || 'Approve all failed')
      }
    } catch (error: any) {
      message.error(error?.message || 'Approve all failed')
      logInfo('Error: ', error)
    } finally {
      onCloseModalProcessing()
    }
  }

  const handleApproveSelected = async () => {
    if (!type) {
      message.error('Vesting type is required')
      return
    }
    const hasSelectedStatus = searchParams.status !== undefined && searchParams.status !== null
    if (!hasSelectedStatus && selectedRowKeys.length === 0) {
      message.warning('Please select items to approve')
      return
    }
    if (isFilterDisabledStatus) {
      message.error('Cannot approve items with status Equal or Cancel Contract')
      return
    }
    try {
      onOpenModalProcessing('Approving selected items...')
      const params: { type: 2 | 3; status?: number; ids?: string[] } = {
        type: mapContextTypeToApiType(type),
      }
      if (hasSelectedStatus) params.status = searchParams.status!
      if (selectedRowKeys.length > 0) params.ids = selectedRowKeys as string[]
      const res = await accountVestingV2Api.approve(params)
      if (res?.data?.success) {
        message.success('Approve selected successfully')
        setSelectedRowKeys([])
        getData(searchParams)
      } else {
        message.error(res?.data?.message || 'Approve selected failed')
      }
    } catch (error: any) {
      message.error(error?.message || 'Approve selected failed')
      logInfo('Error: ', error)
    } finally {
      onCloseModalProcessing()
    }
  }

  const approveMenuItems = [
    {
      key: 'approve-all',
      label: 'Approve All',
      onClick: handleApproveAll,
      disabled: isFilterDisabledStatus,
    },
    {
      key: 'approve-selected',
      label: `Approve Selected (${selectedRowKeys.length})`,
      onClick: handleApproveSelected,
      disabled:
        isFilterDisabledStatus ||
        ((searchParams.status === undefined || searchParams.status === null) &&
          selectedRowKeys.length === 0),
    },
  ]

  const rowSelection: TableRowSelection<AccountVestingTypeV2> = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys)
    },
    getCheckboxProps: (record) => ({
      disabled: isDisabledStatus(record.status),
    }),
  }

  const columns: ColumnsType<AccountVestingTypeV2> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Hash Email',
      dataIndex: 'hashEmail',
      key: 'hashEmail',
      render: (text) => (
        <span style={{ color: '#000' }}>
          {text?.slice(0, 8)}...{text?.slice(-8)}
        </span>
      ),
    },
    {
      title: type ? ALLOCATION_TITLE[type] : 'Allocation',
      dataIndex:
        type === AccountVestingTypeEnum.ACCOUNT_VESTING_HMC_V2
          ? 'allocationHMCV2'
          : 'allocationVenturesV2',
      key: 'allocation',
      align: 'right',
      render: (items: AllocationItemV2[] | undefined, record: AccountVestingTypeV2) => (
        <AllocationDropdownCell items={items ?? []} totalDisplay={record.totalAllocation ?? 0} />
      ),
    },
    {
      title: 'Whitelist Allocation',
      dataIndex:
        type === AccountVestingTypeEnum.ACCOUNT_VESTING_HMC_V2
          ? 'whitelistAllocationHMCV2'
          : 'whitelistAllocationVenturesV2',
      key: 'whitelistAllocation',
      align: 'right',
      render: (items: AllocationItemV2[] | undefined) => {
        const total = items?.reduce((sum, i) => sum + (i.value ?? 0), 0) ?? 0
        return <AllocationDropdownCell items={items ?? []} totalDisplay={total} />
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status: number) => {
        const statusInfo = ACCOUNT_VESTING_STATUS_MAP_V2[status] || {
          label: 'Unknown',
          color: 'default',
        }
        return (
          <Tag color={statusInfo.color} style={{ fontSize: 14 }}>
            {statusInfo.label}
          </Tag>
        )
      },
    },
  ]

  return (
    <div className="main-section vesting-data">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="main-title" style={{ marginBottom: 20 }}>
        {title || 'Vesting Data'}
      </div>

      <div className="vesting-data-toolbar">
        <div className="vesting-data-search">
          <Input
            placeholder="Search by email or hash email"
            value={searchParams.keyword || ''}
            onChange={(e) =>
              onSearch({
                keyword: e.target.value,
              } as Partial<AccountVestingSearchParamsV2>)
            }
            style={{ width: 280 }}
            allowClear
          />
          <Select
            placeholder="Filter by status"
            options={ACCOUNT_VESTING_STATUS_OPTIONS_V2}
            value={searchParams.status}
            onChange={(value) =>
              onSearch({ status: value } as Partial<AccountVestingSearchParamsV2>)
            }
            style={{ width: 160 }}
            allowClear
          />
          <DatePicker
            placeholder="Sign Date From"
            value={dateFrom}
            onChange={handleDateFromChange}
            style={{ width: 200 }}
            format="YYYY-MM-DD HH:mm"
            showTime={{ format: 'HH:mm' }}
            allowClear
          />
          <DatePicker
            placeholder="Sign Date To"
            value={dateTo}
            onChange={handleDateToChange}
            style={{ width: 200 }}
            format="YYYY-MM-DD HH:mm"
            showTime={{ format: 'HH:mm' }}
            allowClear
            disabledDate={(current) => {
              if (dateFrom && current && current.isBefore(dateFrom, 'day')) {
                return true
              }
              return false
            }}
          />
          <Button type="primary" onClick={() => onSearch()}>
            Search
          </Button>
        </div>

        <Space className="vesting-data-actions">
          <ProtectedButton
            icon={<SyncOutlined />}
            onClick={handleSyncData}
            feature={FEATURE.ACCOUNT_VESTING}
            requireCRUD
          >
            Sync Data
          </ProtectedButton>
          <Dropdown menu={{ items: approveMenuItems }} trigger={['click']}>
            <ProtectedButton type="primary" feature={FEATURE.ACCOUNT_VESTING} requireCRUD>
              Approve <DownOutlined />
            </ProtectedButton>
          </Dropdown>
        </Space>
      </div>

      <Card style={{ textAlign: 'left' }}>
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 8 }}>Selected {selectedRowKeys.length} items</span>
            <Button size="small" onClick={() => setSelectedRowKeys([])}>
              Clear
            </Button>
          </div>
        )}
        <Table
          className="vesting-data-table"
          columns={columns}
          dataSource={data}
          rowKey={(record) => record._id}
          loading={loading}
          pagination={initPagination(total)}
          onChange={onChangePagination}
          scroll={{ x: 800 }}
          bordered
          rowSelection={rowSelection}
        />
      </Card>
    </div>
  )
}

export default AccountVestingContentV2
